import { useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useHostContext } from 'src/core/hooks/useHostContext';
import { QueryKeys } from 'src/core/router/constants';
import { ModuleCacheUpdater, PageCacheUpdater } from 'src/modules/contentList/helpers';
import { useGetModuleById } from 'src/modules/modules/hooks/useGetModuleById';
import { DrawerHelper } from 'src/shared/components/drawer/helper';
import { Routes } from 'src/shared/constants/routes';
import { dispatchSaveCustomEvent } from './dispatchSaveCustomEvent';
import { removeContentQueries } from './removeContentQueries';
import { useSubmitMutations } from './useSubmitMutations';
export function useSubmitContent({ isUpdate, resetForm, onSubmitSuccess, setLastContentCreatedQueryParam, isCreateAnotherContentEnabled }) {
    const navigate = useNavigate();
    const { moduleId } = useParams();
    const moduleRequest = useGetModuleById(moduleId);
    const { contentListConceptToggle } = useHostContext();
    const isExtraModule = Boolean(moduleRequest.data?.extra);
    const submitActionRef = useRef(null);
    function handleSubmitButtonClick(action) {
        submitActionRef.current = action;
    }
    function handleSuccess({ payload, isPublished, pageId }) {
        removeContentQueries(pageId);
        if (contentListConceptToggle) {
            PageCacheUpdater.insertPage({ pageId, isPublished, isUpdate, payload });
            ModuleCacheUpdater.updateTotalPages({
                moduleId: payload.page.moduleId,
                isExtraModule,
                totalPages: (totalPages) => totalPages + 1
            });
        }
        else {
            // TODO: Remove after list rollout
            dispatchSaveCustomEvent({ moduleId: payload.page.moduleId, isUpdate, isExtraModule });
        }
        const searchParams = new URLSearchParams({ moduleId: moduleId, pageId });
        if (isExtraModule) {
            searchParams.set(QueryKeys.Extra, 'true');
        }
        if (isCreateAnotherContentEnabled) {
            resetForm();
            setLastContentCreatedQueryParam(`?${searchParams.toString()}`);
            return;
        }
        setLastContentCreatedQueryParam(null);
        const redirectToModulesList = () => {
            navigate({
                pathname: Routes.ListModules,
                search: `?${searchParams.toString()}`
            });
        };
        DrawerHelper.listenerOnClose(redirectToModulesList);
        onSubmitSuccess();
    }
    const { handleSave, handleCreateAndPublish, isSubmitting } = useSubmitMutations({
        isUpdate,
        onSuccess: handleSuccess,
        isCreateAnotherContentEnabled
    });
    function submit(event, handleSubmit) {
        event.preventDefault();
        /*
         * NOTE: This form has two submit buttons, each calling a different method.
         * The clicked button's value is stored in `submitActionRef.current`.
         * A setTimeout with 0 ensures the ref is updated before submission logic runs,
         * as the button is a web component, and we can't access its value directly from the event.
         */
        setTimeout(() => {
            const actionType = submitActionRef.current;
            if (actionType === 'submit') {
                handleSubmit(handleSave)();
                return;
            }
            if (actionType === 'publish') {
                handleSubmit(handleCreateAndPublish)();
            }
        }, 0);
    }
    return {
        submit,
        isSubmitting,
        handleSubmitButtonClick,
        submitAction: submitActionRef.current
    };
}
